import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Store, ExternalLink, Search, Plus, Save, Trash2, Settings } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from '@/contexts/TranslationContext';
import { toast } from 'sonner';

interface UserShop {
  id: string;
  user_id: string;
  shop_name: string;
  shop_url: string;
  shop_description?: string;
  shop_logo_url?: string;
  shopify_domain?: string;
  is_active: boolean;
  is_public: boolean;
  created_at: string;
  profiles?: {
    username: string;
    display_name?: string;
    avatar_url?: string;
  } | null;
}

interface ShopFormData {
  shop_name: string;
  shop_url: string;
  shop_description: string;
  shop_logo_url: string;
  shopify_domain: string;
  shopify_access_token: string;
  is_public: boolean;
  is_active: boolean;
}

export default function Shop() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Set document title and meta description for SEO
  useEffect(() => {
    document.title = 'Shop Pet - Negozi per Animali | AstroPet';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Scopri i migliori negozi per animali su AstroPet. Trova prodotti di qualità per il tuo pet, connettiti con commercianti locali e shop specializzati per cani, gatti e altri animali domestici.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Scopri i migliori negozi per animali su AstroPet. Trova prodotti di qualità per il tuo pet, connettiti con commercianti locali e shop specializzati per cani, gatti e altri animali domestici.';
      document.head.appendChild(meta);
    }
    
    // Set canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', 'https://astropet.it/shop');
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = 'https://astropet.it/shop';
      document.head.appendChild(link);
    }
  }, []);
  const [shops, setShops] = useState<UserShop[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [myShop, setMyShop] = useState<UserShop | null>(null);
  const [formData, setFormData] = useState<ShopFormData>({
    shop_name: '',
    shop_url: '',
    shop_description: '',
    shop_logo_url: '',
    shopify_domain: '',
    shopify_access_token: '',
    is_public: true,
    is_active: true,
  });
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    if (user) {
      fetchShops();
      fetchMyShop();
    }
  }, [user]);

  const fetchShops = async () => {
    try {
      const { data, error } = await supabase
        .from('user_shops')
        .select(`
          id,
          user_id,
          shop_name,
          shop_url,
          shop_description,
          shop_logo_url,
          shopify_domain,
          is_active,
          is_public,
          created_at
        `)
        .eq('is_public', true)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const shopsWithProfiles = await Promise.all(
        (data || []).map(async (shop) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('username, display_name, avatar_url')
            .eq('user_id', shop.user_id)
            .single();

          return {
            ...shop,
            profiles: profile
          };
        })
      );

      setShops(shopsWithProfiles);
    } catch (error) {
      console.error('Error fetching shops:', error);
      toast.error(t('status.error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchMyShop = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('user_shops')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (data) {
        setMyShop(data);
        setFormData({
          shop_name: data.shop_name || '',
          shop_url: data.shop_url || '',
          shop_description: data.shop_description || '',
          shop_logo_url: data.shop_logo_url || '',
          shopify_domain: data.shopify_domain || '',
          shopify_access_token: data.shopify_access_token || '',
          is_public: data.is_public,
          is_active: data.is_active,
        });
      }
    } catch (error) {
      // No shop exists yet, which is fine
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setFormLoading(true);
    try {
      const shopData = {
        user_id: user.id,
        ...formData,
      };

      if (myShop) {
        const { error } = await supabase
          .from('user_shops')
          .update(shopData)
          .eq('id', myShop.id);

        if (error) throw error;
        toast.success(t('shop.updated'));
      } else {
        const { error } = await supabase
          .from('user_shops')
          .insert([shopData]);

        if (error) throw error;
        toast.success(t('shop.connected'));
      }

      fetchMyShop();
      fetchShops();
    } catch (error) {
      console.error('Error saving shop:', error);
      toast.error(myShop ? t('shop.errorUpdating') : t('shop.errorConnecting'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!myShop || !user) return;

    if (!confirm('Sei sicuro di voler eliminare il tuo negozio?')) return;

    setFormLoading(true);
    try {
      const { error } = await supabase
        .from('user_shops')
        .delete()
        .eq('id', myShop.id);

      if (error) throw error;
      toast.success('Negozio eliminato con successo');
      setMyShop(null);
      setFormData({
        shop_name: '',
        shop_url: '',
        shop_description: '',
        shop_logo_url: '',
        shopify_domain: '',
        shopify_access_token: '',
        is_public: true,
        is_active: true,
      });
      fetchShops();
    } catch (error) {
      console.error('Error deleting shop:', error);
      toast.error('Errore nell\'eliminare il negozio');
    } finally {
      setFormLoading(false);
    }
  };

  const filteredShops = shops.filter(shop =>
    shop.shop_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    shop.shop_description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    shop.profiles?.username?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen gradient-secondary p-4">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="text-muted-foreground mt-4">{t('status.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-secondary">
      {/* Header */}
      <div className="glass border-b border-primary/20 sticky top-0 z-40 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
              <Store className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{t('shop.title')}</h1>
              <p className="text-muted-foreground">
                Scopri i negozi della community e gestisci il tuo
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-4">
        <Tabs defaultValue="explore" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 glass border-white/10">
            <TabsTrigger value="explore" className="flex items-center gap-2">
              <Search className="w-4 h-4" />
              Esplora Negozi
            </TabsTrigger>
            <TabsTrigger value="manage" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Il Mio Negozio
            </TabsTrigger>
          </TabsList>

          {/* Explore Shops Tab */}
          <TabsContent value="explore" className="space-y-6">
            {/* Search */}
            <Card className="glass border-white/10">
              <CardContent className="p-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Cerca negozi..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 glass border-white/20 text-white"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Shops Grid */}
            {filteredShops.length === 0 ? (
              <Card className="glass border-white/10">
                <CardContent className="p-8 text-center">
                  <Store className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">
                    Nessun negozio trovato
                  </h3>
                  <p className="text-muted-foreground">
                    Non ci sono negozi pubblici al momento
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredShops.map((shop) => (
                  <Card key={shop.id} className="glass border-white/10 overflow-hidden group hover:border-primary/30 transition-all">
                    <CardHeader className="p-0">
                      {shop.shop_logo_url ? (
                        <div className="h-48 bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                          <img
                            src={shop.shop_logo_url}
                            alt={shop.shop_name}
                            className="max-h-full max-w-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className="h-48 bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                          <Store className="w-16 h-16 text-primary" />
                        </div>
                      )}
                    </CardHeader>

                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white group-hover:text-primary transition-colors">
                            {shop.shop_name}
                          </h3>
                          {shop.shop_description && (
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                              {shop.shop_description}
                            </p>
                          )}
                        </div>
                        <Badge variant="secondary" className="ml-2">
                          <Store className="w-3 h-3 mr-1" />
                          {shop.shopify_domain ? 'Shopify' : 'Shop'}
                        </Badge>
                      </div>

                      {/* Owner info */}
                      <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-white/5">
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={shop.profiles?.avatar_url} />
                          <AvatarFallback>
                            {shop.profiles?.display_name?.[0] || shop.profiles?.username[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">
                            {shop.profiles?.display_name || shop.profiles?.username}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Proprietario
                          </p>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button
                          onClick={() => navigate(`/app/shop/${shop.id}`)}
                          className="flex-1 bg-primary hover:bg-primary/90"
                        >
                          <Store className="w-4 h-4 mr-2" />
                          Visualizza Prodotti
                        </Button>
                        <Button
                          asChild
                          variant="outline"
                          size="sm"
                        >
                          <a
                            href={shop.shop_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Manage Shop Tab */}
          <TabsContent value="manage" className="space-y-6">
            {myShop && (
              <Card className="glass border-white/10">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white">Gestisci Prodotti</CardTitle>
                    <Button
                      onClick={() => navigate(`/app/shop/manage/${myShop.id}`)}
                      className="bg-primary hover:bg-primary/90"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Gestisci Prodotti
                    </Button>
                  </div>
                </CardHeader>
              </Card>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <Card className="glass border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Store className="w-5 h-5" />
                    {myShop ? 'Modifica il tuo Negozio' : 'Collega il tuo Negozio'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Shop Name */}
                  <div className="space-y-2">
                    <Label htmlFor="shop_name" className="text-white">
                      {t('shop.shopName')} *
                    </Label>
                    <Input
                      id="shop_name"
                      value={formData.shop_name}
                      onChange={(e) => setFormData({ ...formData, shop_name: e.target.value })}
                      placeholder="es. Pet Store di Mario"
                      className="glass border-white/20 text-white"
                      required
                    />
                  </div>

                  {/* Shop URL */}
                  <div className="space-y-2">
                    <Label htmlFor="shop_url" className="text-white">
                      {t('shop.shopUrl')} *
                    </Label>
                    <Input
                      id="shop_url"
                      type="url"
                      value={formData.shop_url}
                      onChange={(e) => setFormData({ ...formData, shop_url: e.target.value })}
                      placeholder="https://mio-sito.com"
                      className="glass border-white/20 text-white"
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      L'URL del tuo sito web o negozio online
                    </p>
                  </div>

                  {/* Shop Description */}
                  <div className="space-y-2">
                    <Label htmlFor="shop_description" className="text-white">
                      {t('shop.shopDescription')}
                    </Label>
                    <Textarea
                      id="shop_description"
                      value={formData.shop_description}
                      onChange={(e) => setFormData({ ...formData, shop_description: e.target.value })}
                      placeholder="Descrivi il tuo negozio e i prodotti che vendi..."
                      className="glass border-white/20 text-white resize-none"
                      rows={4}
                    />
                  </div>

                  {/* Logo URL */}
                  <div className="space-y-2">
                    <Label htmlFor="shop_logo_url" className="text-white">
                      {t('shop.logoUrl')}
                    </Label>
                    <Input
                      id="shop_logo_url"
                      type="url"
                      value={formData.shop_logo_url}
                      onChange={(e) => setFormData({ ...formData, shop_logo_url: e.target.value })}
                      placeholder="https://esempio.com/logo.png"
                      className="glass border-white/20 text-white"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="glass border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Impostazioni Privacy</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Public Toggle */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label className="text-white">{t('shop.makePublic')}</Label>
                      <p className="text-sm text-muted-foreground">
                        Permetti ad altri utenti di vedere il tuo negozio
                      </p>
                    </div>
                    <Switch
                      checked={formData.is_public}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_public: checked })}
                    />
                  </div>

                  {/* Active Toggle */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label className="text-white">{t('shop.isActive')}</Label>
                      <p className="text-sm text-muted-foreground">
                        Il negozio è attualmente operativo
                      </p>
                    </div>
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  type="submit"
                  disabled={formLoading}
                  className="flex-1 bg-primary hover:bg-primary/90"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {formLoading ? t('action.saving') : (myShop ? t('action.save') : 'Collega Negozio')}
                </Button>

                {myShop && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleDelete}
                    disabled={formLoading}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Elimina
                  </Button>
                )}
              </div>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}